import { useState } from "react"
import { useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"
import { Box, DialogContent, FormHelperText, IconButton, Typography } from "@mui/material"
import { faCircleInfo, faClock, faTruck } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import { MAX_ORDER_COMMENT_LENGTH } from "../../../../../../constants/inputValidationDefaults"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { useDateTime } from "../../../../../../modules/localization/useDateTime"
import { FormikTextField } from "../../../../forms/Formik"
import { SelectableRadioCard } from "../../../../selectableRadioCard/SelectableRadioCard"
import { StandardDialog } from "../../../../dialogs/StandardDialog"

import type { TakeawayDeliveryLocation } from "../takeawayTypes"
import type { CheckoutFormValues } from "./takeawayCheckoutLib"
import { TakeawayCheckoutSectionHeader } from "./TakeawayCheckoutSectionHeader"

const useStyles = makeStyles()((theme) => ({
    root: {
        display: "flex",
        flexDirection: "column",
    },
    locationList: {
        display: "flex",
        flexDirection: "column",
        gap: ".75rem",
    },
    locationName: {
        fontWeight: 700,
        marginBottom: ".25rem",
    },
    iconWrap: {
        display: "inline-flex",
        whiteSpace: "nowrap",
        verticalAlign: "middle",
    },
    infoButton: {
        padding: 0,
        position: "relative",
        bottom: ".125rem",
        color: theme.palette.tailwind.slate[400],
        transition: "all 0.3s ease-out",
        marginLeft: ".5rem",
        fontSize: "1rem",
        "&:hover": {
            color: theme.palette.tailwind.slate[500],
        },
    },
    timeRow: {
        display: "flex",
        alignItems: "center",
        gap: ".375rem",
    },
    timeIcon: {
        color: theme.palette.tailwind.gray[400],
    },
    commentSection: {
        marginTop: "1rem",
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
    },
    commentLabel: {
        fontWeight: 700,
    },
}))

type LocationCardProps = Readonly<{
    location: TakeawayDeliveryLocation
    isSelected: boolean
    onSelect: (id: number) => void
}>

function LocationCard({ location, isSelected, onSelect }: LocationCardProps) {
    const { classes } = useStyles()
    const dt = useDateTime()
    const [descriptionOpen, setDescriptionOpen] = useState(false)

    return (
        <>
            <SelectableRadioCard isSelected={isSelected} onSelect={() => onSelect(location.id)}>
                <Typography variant="body1" className={classes.locationName}>
                    {location.displayName}
                    {location.description && (
                        <Typography className={classes.iconWrap}>
                            <IconButton
                                className={classes.infoButton}
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    setDescriptionOpen(true)
                                }}
                            >
                                <FontAwesomeIcon icon={faCircleInfo} />
                            </IconButton>
                        </Typography>
                    )}
                </Typography>

                {location.timeInterval &&
                    (() => {
                        const formatted = dt.formatTimeInterval(location.timeInterval)
                        if (!formatted) return null
                        const { start, end } = formatted
                        return (
                            <Box className={classes.timeRow}>
                                <FontAwesomeIcon icon={faClock} className={classes.timeIcon} />
                                <LocalizedStrict id="takeaway-checkout-delivery-location-pickup-label">
                                    <Typography variant="body2">Pick up:</Typography>
                                </LocalizedStrict>
                                <LocalizedStrict
                                    id="takeaway-checkout-delivery-location-pickup-time"
                                    vars={{ start, end }}
                                >
                                    <Typography variant="body2" fontWeight={700}>
                                        {start} - {end}
                                    </Typography>
                                </LocalizedStrict>
                            </Box>
                        )
                    })()}
            </SelectableRadioCard>

            {descriptionOpen && location.description && (
                <StandardDialog
                    open
                    dialogTitle={location.displayName}
                    onClose={() => setDescriptionOpen(false)}
                    scroll="body"
                >
                    <DialogContent>
                        <Typography variant="body1">{location.description}</Typography>
                    </DialogContent>
                </StandardDialog>
            )}
        </>
    )
}

type TakeawayDeliverySectionProps = Readonly<{
    locations: TakeawayDeliveryLocation[]
    selectedLocationId: number | null
    onLocationSelect: (id: number) => void
}>

export function TakeawayDeliverySection({
    locations,
    selectedLocationId,
    onLocationSelect,
}: TakeawayDeliverySectionProps) {
    const { classes } = useStyles()
    const { errors, touched, submitCount, values } = useFormikContext<CheckoutFormValues>()
    const error = touched.deliveryLocationId || submitCount > 0 ? errors.deliveryLocationId : undefined
    const charsRemaining = MAX_ORDER_COMMENT_LENGTH - (values.orderComment?.length ?? 0)

    return (
        <Box className={classes.root}>
            <TakeawayCheckoutSectionHeader
                icon={faTruck}
                title={
                    <LocalizedStrict id="takeaway-confirm-order-delivery-section-title">
                        <span>Delivery</span>
                    </LocalizedStrict>
                }
            />

            {locations.length > 0 && (
                <Box className={classes.locationList}>
                    {locations.map((location) => (
                        <LocationCard
                            key={location.id}
                            location={location}
                            isSelected={selectedLocationId === location.id}
                            onSelect={onLocationSelect}
                        />
                    ))}
                </Box>
            )}

            {error && <FormHelperText error>{error}</FormHelperText>}

            <Box className={classes.commentSection}>
                <LocalizedStrict id="takeaway-confirm-order-comment-title">
                    <Typography variant="body1" className={classes.commentLabel}>
                        Order comment:
                    </Typography>
                </LocalizedStrict>
                <LocalizedStrict
                    id="takeaway-confirm-order-comment-placeholder"
                    attrs={{ label: true, helperText: true }}
                    vars={{ charsRemaining }}
                >
                    <FormikTextField<CheckoutFormValues>
                        name="orderComment"
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={4}
                        inputProps={{ maxLength: MAX_ORDER_COMMENT_LENGTH }}
                        label="Write a comment (optional)"
                        helperText={`Remaining characters: ${charsRemaining}`}
                    />
                </LocalizedStrict>
            </Box>
        </Box>
    )
}
